/**
 * Kohlrabi — Krieger (Iron Guard). REFERENZ-PAINTER fuer den LMAO-Style (BATTLE-14):
 * mehrschichtig, Volumen-Gradient, Rim-Light, Specular, Persoenlichkeit "stoischer Veteran".
 * Sternstufe: 2★ Metallband, 3★ leuchtende Runen auf dem Korpus.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'

export const paintKohlrabi: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  ctx.save()
  ctx.lineWidth = Math.max(2, r * 0.12)
  ctx.strokeStyle = colors.outline
  ctx.lineJoin = 'round'

  // --- Stummel-"Ruestungsdorne" (hinter dem Korpus), je mit eigenem Volumen ---
  for (const [dx, ang] of [[-0.45, -0.5], [0, 0], [0.45, 0.5]] as const) {
    const sx = x + dx * r
    const sy = y - r * 0.85
    ctx.save()
    ctx.translate(sx, sy)
    ctx.rotate(ang)
    const g = ctx.createLinearGradient(-r * 0.14, 0, r * 0.14, 0)
    g.addColorStop(0, lighten(colors.accent, 14))
    g.addColorStop(0.5, colors.accent)
    g.addColorStop(1, darken(colors.accent, 18))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.ellipse(0, 0, r * 0.15, r * 0.34, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    // kleine Schnittflaeche oben (hellerer Deckel)
    ctx.fillStyle = lighten(colors.body, 20)
    ctx.beginPath()
    ctx.ellipse(0, -r * 0.26, r * 0.09, r * 0.05, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // --- Korpus: kugelig-massiv mit Volumen ---
  ctx.fillStyle = volumeGradient(ctx, x, y, r, colors.body)
  ctx.beginPath()
  ctx.ellipse(x, y, r * 0.96, r * 0.9, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()

  // Wurzel-Aederchen (Kohlrabi-Charakteristik): feine helle Bahnen unten
  ctx.save()
  ctx.strokeStyle = rgba(lighten(colors.body, 26), dead ? 0.25 : 0.6)
  ctx.lineWidth = Math.max(1, r * 0.05)
  for (const dx of [-0.42, -0.05, 0.36]) {
    ctx.beginPath()
    ctx.moveTo(x + dx * r, y + r * 0.85)
    ctx.quadraticCurveTo(x + dx * r * 1.5, y + r * 0.45, x + dx * r * 0.7, y + r * 0.05)
    ctx.stroke()
  }
  ctx.restore()

  // --- Sternstufen-Eskalation am Korpus ---
  if (tier >= 2 && !dead) {
    // Metallband ("Guertel") mit Nieten
    const bandY = y + r * 0.34
    const g = ctx.createLinearGradient(x, bandY - r * 0.1, x, bandY + r * 0.1)
    g.addColorStop(0, '#d7dee6')
    g.addColorStop(0.5, '#9aa4ae')
    g.addColorStop(1, '#6a737d')
    ctx.fillStyle = g
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.ellipse(x, bandY, r * 0.88, r * 0.16, 0, 0, Math.PI)
    ctx.ellipse(x, bandY, r * 0.88, r * 0.16, 0, Math.PI, 0, true)
    ctx.fill()
    ctx.stroke()
    ctx.fillStyle = '#3f464e'
    for (const dx of [-0.55, 0, 0.55]) {
      ctx.beginPath()
      ctx.arc(x + dx * r * 0.8, bandY + r * 0.02, r * 0.045, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  if (tier >= 3 && !dead) {
    // leuchtende Runen (docs/11: "leuchtende Runen" ab 3★) — pulsieren leicht mit castPulse
    const runeAlpha = 0.7 + pose.castPulse * 0.3
    ctx.save()
    ctx.strokeStyle = rgba('#ffd94a', runeAlpha)
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.shadowColor = '#ffd94a'
    ctx.shadowBlur = r * 0.22
    for (const [dx, dy] of [[-0.45, -0.1], [0.42, -0.05], [0, 0.18]] as const) {
      const rx = x + dx * r
      const ry = y + dy * r
      ctx.beginPath()
      ctx.moveTo(rx, ry - r * 0.12)
      ctx.lineTo(rx + r * 0.08, ry)
      ctx.lineTo(rx - r * 0.06, ry + r * 0.06)
      ctx.lineTo(rx + r * 0.04, ry + r * 0.14)
      ctx.stroke()
    }
    ctx.restore()
  }

  // --- Licht: Rim oben-links + Specular ---
  if (!dead) {
    rimLight(ctx, x, y, r * 0.96, r * 0.9, 'rgba(255, 252, 240, 0.45)', Math.max(1.5, r * 0.07))
    specular(ctx, x - r * 0.34, y - r * 0.42, r * 0.16, 0.5)
  }

  ctx.restore()
}
